import { useMemo } from "react";
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameMonth, isToday, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { STATUS, toKey } from "@/lib/format";

const WEEK = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const buildIndex = (events) => {
  const idx = {};
  events.forEach((ev) => {
    for (let d = parseISO(ev.setup_date); toKey(d) <= ev.teardown_date; d = addDays(d, 1)) {
      const k = toKey(d);
      const kind = k === ev.setup_date ? "montagem" : k === ev.teardown_date ? "desmontagem" : k === ev.event_date ? "evento" : "andamento";
      (idx[k] = idx[k] || []).push({ ev, kind: k === ev.event_date ? "evento" : kind });
    }
  });
  return idx;
};

const KIND = { montagem: "Mont.", desmontagem: "Desm.", evento: "Evento", andamento: "" };

export const MonthCalendar = ({ month, setMonth, events, onSelect }) => {
  const idx = useMemo(() => buildIndex(events), [events]);
  const days = [];
  for (let d = startOfWeek(startOfMonth(month)); d <= endOfWeek(endOfMonth(month)); d = addDays(d, 1)) days.push(d);

  return (
    <div data-testid="calendar-view-container" className="rounded-md border border-slate-800/80 bg-slate-900/60 backdrop-blur-md">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <h2 data-testid="calendar-month-label" className="font-display text-2xl font-bold capitalize">{format(month, "MMMM yyyy", { locale: ptBR })}</h2>
        <div className="flex gap-1">
          <button data-testid="calendar-prev-month-button" onClick={() => setMonth(addMonths(month, -1))} className="rounded-sm border border-slate-700 p-2 transition-colors hover:bg-slate-800"><ChevronLeft size={16} /></button>
          <button data-testid="calendar-today-button" onClick={() => setMonth(new Date())} className="rounded-sm border border-slate-700 px-3 font-mono text-xs uppercase transition-colors hover:bg-slate-800">Hoje</button>
          <button data-testid="calendar-next-month-button" onClick={() => setMonth(addMonths(month, 1))} className="rounded-sm border border-slate-700 p-2 transition-colors hover:bg-slate-800"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-800 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        {WEEK.map((w) => <div key={w} className="px-2 py-2">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const k = toKey(d);
          const items = idx[k] || [];
          return (
            <div key={k} data-testid={`calendar-day-${k}`} className={`min-h-[64px] border-b border-r border-slate-800/70 p-1 sm:min-h-[104px] sm:p-1.5 ${isSameMonth(d, month) ? "" : "opacity-35"}`}>
              <div className={`mb-1 inline-grid h-6 w-6 place-items-center rounded-sm font-mono text-xs ${isToday(d) ? "bg-amber-500 font-bold text-white" : "text-slate-400"}`}>{format(d, "d")}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map(({ ev, kind }) => (
                  <button key={ev.id} onClick={() => onSelect(ev)} data-testid={`calendar-event-${ev.id}-${k}`} title={ev.title}
                    className={`flex w-full items-center gap-1 truncate rounded-sm border px-1 py-0.5 text-left text-[10px] transition-transform hover:-translate-y-px sm:text-xs ${STATUS[ev.status].cls}`}>
                    <span className={`hidden h-1.5 w-1.5 shrink-0 rounded-full sm:block ${STATUS[ev.status].dot}`} />
                    {KIND[kind] && <span className="hidden font-mono text-[9px] uppercase opacity-80 lg:inline">{KIND[kind]}</span>}
                    <span className="truncate">{ev.title}</span>
                  </button>
                ))}
                {items.length > 3 && <div className="px-1 font-mono text-[10px] text-slate-500">+{items.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
